import React, { useEffect, useState } from "react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import IdentityVerificationModalHeader from "@/components/ui/identity_verification_modal_header";
import { Body, Link } from "@sprout/typography";
import useTranslation from "@hooks/use_translation";
import { PrimaryButton } from "@sprout/buttons";
import {
  IDENTITY_VERIFICATION_CALLBACK_PARAMS,
  type IdentityVerificationPartner,
  identityVerificationPartnerConfig,
  isIdentityVerificationPartner,
} from "@/utils/identity_verification_provider";

const isProviderCompletionRedirect = (urlParams: URLSearchParams): boolean =>
  urlParams.get("verification_session_complete") === "true";

const partnerFromRedirect = (urlParams: URLSearchParams): IdentityVerificationPartner => {
  const rawPartner = urlParams.get("partner");

  // CLEAR sessions created before partner-aware redirects only have the
  // completion flag; an unknown partner is user-controlled, not a crash.
  return isIdentityVerificationPartner(rawPartner) ? rawPartner : "clear";
};

// Scrub the completion flag and every provider's callback params — they carry
// verification identifiers that shouldn't linger in history, bookmarks, or
// Referer headers. Unrelated params are left untouched.
const stripVerificationParamsFromUrl = () => {
  const newUrl = new URL(window.location.href);
  newUrl.searchParams.delete("verification_session_complete");
  newUrl.searchParams.delete("partner");
  // Strip the union across providers: the partner param can be absent or mangled while provider params remain.
  IDENTITY_VERIFICATION_CALLBACK_PARAMS.forEach((param) => newUrl.searchParams.delete(param));
  if (newUrl.href === window.location.href) return;

  // Preserve Inertia's history state; replacing it with {} breaks back-navigation.
  window.history.replaceState(window.history.state, "", newUrl.toString());
};

// Inertia's initial load rewrites the URL from its stored page object after we
// mount, resurrecting the params we just stripped. Strip once more when that
// happens (the first inertia:navigate), then detach so a later page's own
// `status`/`subject` params are left alone. Returns the listener cleanup.
const stripVerificationParamsAfterInertiaSettles = (): (() => void) => {
  const stripOnce = () => {
    stripVerificationParamsFromUrl();
    document.removeEventListener("inertia:navigate", stripOnce);
  };
  document.addEventListener("inertia:navigate", stripOnce);

  return () => document.removeEventListener("inertia:navigate", stripOnce);
};

const IdentityVerificationConfirmationModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [partner, setPartner] = useState<string | null>(null);
  const { t } = useTranslation("identity_verification");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!isProviderCompletionRedirect(urlParams)) return;

    setPartner(partnerFromRedirect(urlParams));
    setIsOpen(true);

    stripVerificationParamsFromUrl();
    return stripVerificationParamsAfterInertiaSettles();
  }, []);

  const partnerConfig = isOpen ? identityVerificationPartnerConfig(partner) : null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {partnerConfig && (
        <DialogContent className="sm:max-w-md p-0 rounded-lg shadow-lg">
          <div className="px-6 pt-9">
            <IdentityVerificationModalHeader className="mb-0" partner={partner} />
          </div>

          <div className="px-6">
            <Body medium className="!text-[18px]">
              {t("confirmation.title")}
            </Body>
          </div>

          <div className="px-6 mb-4">
            <Body className="text-base text-gray-900">
              {`${t("confirmation.message_intro", { provider_name: partnerConfig.displayName })} ${t("confirmation.message_prefix", { provider_name: partnerConfig.displayName })} `}
              <Link href="/account">{t("confirmation.account_profile")}</Link>.
            </Body>
          </div>

          <div className="flex justify-end px-6 pb-6">
            <PrimaryButton size="medium" onClick={() => setIsOpen(false)}>
              {t("confirmation.done")}
            </PrimaryButton>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default IdentityVerificationConfirmationModal;
